'use client'

import { useRouter } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { logoutAction } from './actions'
import { clearAuthStore } from '@/src/shared/lib/auth/clearAuthStore'

interface UseLogoutParams {
  locale: string
  redirectPath?: string
}

export function useLogout({ locale, redirectPath }: UseLogoutParams) {
  const router = useRouter()
  const queryClient = useQueryClient()

  const handleLogout = async () => {
    console.log('🚪 [Logout] Starting logout process...', { locale, redirectPath })

    try {
      // 1. Очищаем React Query cache
      console.log('🗑️ [Logout] Step 1: Clearing React Query cache...')
      queryClient.clear()
      console.log('✅ [Logout] React Query cache cleared')

      // 2. КРИТИЧНО: Очищаем auth stores СРАЗУ
      clearAuthStore()

      // 3. Очищаем localStorage/sessionStorage
      if (typeof window !== 'undefined') {
        console.log('🗑️ [Logout] Step 2: Clearing localStorage/sessionStorage...')
        
        const storageKeys = ['user', 'auth', 'preferences', 'cart', 'drafts', 'auth-storage']
        
        storageKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            console.log(`  - Removing localStorage key: ${key}`)
            localStorage.removeItem(key)
          }
          if (sessionStorage.getItem(key)) {
            console.log(`  - Removing sessionStorage key: ${key}`)
            sessionStorage.removeItem(key)
          }
        })
        
        console.log('✅ [Logout] Storage cleared')
      }

      // 4. Отключаем WebSocket соединения (если есть)
      if (typeof window !== 'undefined' && (window as any).socket) {
        console.log('🔌 [Logout] Step 3: Disconnecting WebSocket...')
        ;(window as any).socket.disconnect()
        delete (window as any).socket
        console.log('✅ [Logout] WebSocket disconnected')
      } else {
        console.log('ℹ️ [Logout] Step 3: No WebSocket connection found')
      }

      // 5. КРИТИЧНО: Вызываем server action и ЖДЁМ завершения
      console.log('🍪 [Logout] Step 4: Calling server action to clear cookies...')
      const result = await logoutAction()
      console.log('✅ [Logout] Server action completed:', result)

      // Проверяем результат
      if (!result.success) {
        console.error('⚠️ [Logout] Server action failed, but continuing with logout')
      }

      // 6. КРИТИЧНО: Даём серверу время обработать удаление cookies
      console.log('⏳ [Logout] Waiting for server to process...')
      await new Promise(resolve => setTimeout(resolve, 100))

      // 7. Перенаправляем с полной перезагрузкой страницы
      // Проверяем, что redirectPath не undefined и не строка "undefined"
      const hasValidRedirectPath = redirectPath && redirectPath !== 'undefined' && typeof redirectPath === 'string' && redirectPath.trim() !== ''
      const path = hasValidRedirectPath ? `/${locale}/${redirectPath}` : `/${locale}`
      console.log(`🔄 [Logout] Step 5: Hard redirect to: ${path}`, { 
        redirectPath, 
        hasValidRedirectPath,
        locale 
      })
      
      // КРИТИЧНО: Используем window.location для полной перезагрузки
      window.location.href = path
      
      console.log('✅ [Logout] Logout process completed successfully')
    } catch (error) {
      console.error('❌ [Logout] Error during logout:', error)
      console.error('❌ [Logout] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error
      })
      
      // Всё равно перенаправляем, даже если была ошибка
      const fallbackPath = `/${locale}`
      console.log(`⚠️ [Logout] Fallback hard redirect to: ${fallbackPath}`)
      window.location.href = fallbackPath
    }
  }

  return { handleLogout }
}
